import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { SalesQualificationStepProps } from '@/types/onboarding/salesQualification';
import { formatNumber } from '../utils';

export const CommunityProfile: React.FC<SalesQualificationStepProps> = ({ data, onUpdate }) => {
  const updateFormData = (field: string, value: unknown) => {
    onUpdate({ ...data, [field]: value });
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Community Profile</h2>
        <p className="text-gray-600">
          Tell us about your community - this information helps us prepare a personalized enterprise
          consultation
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Community Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Community Details</CardTitle>
            <CardDescription>Basic information about your community</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="communityName">Community Name *</Label>
              <Input
                id="communityName"
                value={data.communityName || ''}
                onChange={(e) => updateFormData('communityName', e.target.value)}
                placeholder="e.g., Sunset Hills HOA"
                required
              />
            </div>

            <div>
              <Label htmlFor="communityType">Community Type *</Label>
              <Select
                value={data.communityType || ''}
                onValueChange={(value) => updateFormData('communityType', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select community type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hoa">Homeowners Association (HOA)</SelectItem>
                  <SelectItem value="landowner">Land Owner Association</SelectItem>
                  <SelectItem value="condo">Condominium Association</SelectItem>
                  <SelectItem value="townhome">Townhome Community</SelectItem>
                  <SelectItem value="cooperative">Housing Cooperative</SelectItem>
                  <SelectItem value="planned_community">Planned Community</SelectItem>
                  <SelectItem value="mobile_home">Mobile Home Community</SelectItem>
                  <SelectItem value="mixed_use">Mixed-Use Development</SelectItem>
                  <SelectItem value="master_planned">Master Planned Community</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Total Units/Parcels *</Label>
              <div className="mt-2">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={data.organizationSize ?? ''}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, '');
                    const formatted = formatNumber(raw);
                    updateFormData('organizationSize', formatted);
                  }}
                  placeholder="Enter total units (e.g., 2,500)"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Total number of units, lots, or parcels in your community
                </p>
              </div>
            </div>

            <div>
              <Label htmlFor="establishedYear">Year Established</Label>
              <Input
                id="establishedYear"
                value={data.establishedYear || ''}
                onChange={(e) => updateFormData('establishedYear', e.target.value)}
                placeholder="e.g., 1995"
              />
            </div>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Contact Information</CardTitle>
            <CardDescription>How we'll reach out for your enterprise consultation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="contactName">Your Name *</Label>
              <Input
                id="contactName"
                value={data.contactName || ''}
                onChange={(e) => updateFormData('contactName', e.target.value)}
                placeholder="e.g., John Smith"
                required
              />
            </div>

            <div>
              <Label htmlFor="contactTitle">Your Title/Position *</Label>
              <Input
                id="contactTitle"
                value={data.contactTitle || ''}
                onChange={(e) => updateFormData('contactTitle', e.target.value)}
                placeholder="e.g., Board President, Property Manager"
                required
              />
            </div>

            <div>
              <Label htmlFor="contactEmail">Email Address *</Label>
              <Input
                id="contactEmail"
                type="email"
                value={data.contactEmail || ''}
                onChange={(e) => updateFormData('contactEmail', e.target.value)}
                placeholder="john@example.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="contactPhone">Phone Number *</Label>
              <Input
                id="contactPhone"
                type="tel"
                value={data.contactPhone || ''}
                onChange={(e) => updateFormData('contactPhone', e.target.value)}
                placeholder="(555) 123-4567"
                required
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Location Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Community Location</CardTitle>
          <CardDescription>
            Location helps us understand local regulations and market opportunities
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="state">State *</Label>
              <Input
                id="state"
                value={data.state || ''}
                onChange={(e) => updateFormData('state', e.target.value)}
                placeholder="e.g., Montana"
                required
              />
            </div>
            <div>
              <Label htmlFor="city">City *</Label>
              <Input
                id="city"
                value={data.city || ''}
                onChange={(e) => updateFormData('city', e.target.value)}
                placeholder="e.g., Billings"
                required
              />
            </div>
            <div>
              <Label htmlFor="zipCode">ZIP Code</Label>
              <Input
                id="zipCode"
                value={data.zipCode || ''}
                onChange={(e) => updateFormData('zipCode', e.target.value)}
                placeholder="e.g., 59101"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Annual Budget Preview */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Annual Financial Overview</CardTitle>
          <CardDescription>
            High-level financial information for enterprise planning
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Annual Budget Range</Label>
              <Select
                value={data.annualRevenue}
                onValueChange={(value) => updateFormData('annualRevenue', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-500k">Under $500K</SelectItem>
                  <SelectItem value="500k-1m">$500K - $1M</SelectItem>
                  <SelectItem value="1m-5m">$1M - $5M</SelectItem>
                  <SelectItem value="5m-10m">$5M - $10M</SelectItem>
                  <SelectItem value="10m-25m">$10M - $25M</SelectItem>
                  <SelectItem value="over-25m">Over $25M</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Technology/Operations Budget Range</Label>
              <Select
                value={data.budgetRange}
                onValueChange={(value) => updateFormData('budgetRange', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select range" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="under-25k">Under $25K annually</SelectItem>
                  <SelectItem value="25k-50k">$25K - $50K annually</SelectItem>
                  <SelectItem value="50k-100k">$50K - $100K annually</SelectItem>
                  <SelectItem value="100k-250k">$100K - $250K annually</SelectItem>
                  <SelectItem value="250k-500k">$250K - $500K annually</SelectItem>
                  <SelectItem value="over-500k">Over $500K annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Help Text */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p className="text-sm text-purple-700">
          <strong>Why we need this information:</strong> These details help us prepare a
          personalized enterprise consultation agenda, calculate potential ROI, and ensure we
          discuss opportunities most relevant to your community size and complexity.
        </p>
      </div>
    </div>
  );
};
